/**
 * AdminEmailInviteTool
 * Bulk onboarding-uitnodigingen sturen via de emailLeads collectie.
 * Elke ingevoerde e-mail triggert automatisch de Cloud Function
 * onEmailLeadCreated → welkomstmail met link naar /?onboarding=1
 */

import React, { useState } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, query, where, serverTimestamp, setDoc, doc } from 'firebase/firestore';

interface InviteRow {
  email: string;
  displayName: string;
  status: 'idle' | 'sending' | 'sent' | 'already_sent' | 'error';
  error?: string;
}

function parseEmailLines(raw: string): { email: string; displayName: string }[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      // Support "Naam <email@x.nl>" or just "email@x.nl"
      const match = line.match(/^(.+?)\s*<([^>]+)>$/);
      if (match) {
        return { displayName: match[1].trim(), email: match[2].trim().toLowerCase() };
      }
      return { displayName: '', email: line.toLowerCase() };
    })
    .filter(({ email }) => email.includes('@'));
}

const AdminEmailInviteTool: React.FC = () => {
  const [rawInput, setRawInput] = useState('');
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState<'input' | 'preview' | 'done'>('input');

  const handlePreview = () => {
    const parsed = parseEmailLines(rawInput);
    if (parsed.length === 0) return;
    setRows(
      parsed.map((p) => ({
        email: p.email,
        displayName: p.displayName,
        status: 'idle',
      }))
    );
    setStep('preview');
  };

  const updateRow = (i: number, patch: Partial<InviteRow>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const handleSendAll = async () => {
    setSending(true);

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.status === 'sent' || row.status === 'already_sent') continue;

      updateRow(i, { status: 'sending' });

      try {
        // Check if already invited
        const existing = await getDocs(
          query(collection(db, 'emailLeads'), where('email', '==', row.email))
        );
        const alreadySent = existing.docs.some((d) => d.data().welcomeEmailSentAt);
        if (alreadySent) {
          // Zorg dat ze wel in allowedUsers staan ook als ze al een mail hadden
          await setDoc(doc(db, 'allowedUsers', row.email), {
            email: row.email,
            displayName: row.displayName || '',
            addedAt: serverTimestamp(),
            addedBy: 'admin_bulk_invite',
          }, { merge: true });
          updateRow(i, { status: 'already_sent' });
          continue;
        }

        // 1. Voeg toe aan allowedUsers whitelist
        await setDoc(doc(db, 'allowedUsers', row.email), {
          email: row.email,
          displayName: row.displayName || '',
          addedAt: serverTimestamp(),
          addedBy: 'admin_bulk_invite',
        });

        // 2. Voeg toe aan emailLeads → triggert welkomstmail via Cloud Function
        await addDoc(collection(db, 'emailLeads'), {
          email: row.email,
          displayName: row.displayName || 'ondernemer',
          createdAt: serverTimestamp(),
          source: 'admin_bulk_invite',
        });

        updateRow(i, { status: 'sent' });
      } catch (err: any) {
        updateRow(i, { status: 'error', error: err?.message || 'Onbekende fout' });
      }
    }

    setSending(false);
    setStep('done');
  };

  const reset = () => {
    setRawInput('');
    setRows([]);
    setStep('input');
  };

  const sentCount = rows.filter((r) => r.status === 'sent').length;
  const alreadyCount = rows.filter((r) => r.status === 'already_sent').length;
  const errorCount = rows.filter((r) => r.status === 'error').length;

  return (
    <div
      style={{
        background: '#FFFFFF',
        borderRadius: 14,
        border: '1px solid #E0DDD8',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: '#2E5F56',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          ✉️
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF' }}>
            Onboarding uitnodigingen sturen
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
            Voer e-mailadressen in → automatische welkomstmail met onboarding-link
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 20px 24px' }}>
        {/* ── Stap 1: Invoer ── */}
        {step === 'input' && (
          <>
            <div
              style={{
                background: 'rgba(46,95,86,0.06)',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 16,
                fontSize: 12.5,
                color: '#4F6660',
                lineHeight: 1.6,
              }}
            >
              <strong>Hoe invoeren:</strong>
              <br />
              • Één e-mailadres per regel: <code>jan@bedrijf.nl</code>
              <br />
              • Of met naam: <code>Jan de Vries &lt;jan@bedrijf.nl&gt;</code>
              <br />
              <br />
              <strong>Wat er automatisch gebeurt:</strong>
              <br />
              ✓ E-mail toegevoegd aan toegangslijst (kan inloggen)
              <br />
              ✓ Welkomstmail met onboarding-link verstuurd
            </div>

            <textarea
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              placeholder={
                'jan@bedrijf.nl\nMaria Jansen <maria@startup.nl>\npeter@bureau.nl'
              }
              rows={8}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: 10,
                border: '1.5px solid #E0DDD8',
                fontSize: 13.5,
                fontFamily: 'ui-monospace, monospace',
                color: '#1C1C1C',
                background: '#FAFAF8',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.7,
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <span style={{ fontSize: 12, color: '#9A9A9A' }}>
                {parseEmailLines(rawInput).length} e-mailadressen herkend
              </span>
              <button
                onClick={handlePreview}
                disabled={parseEmailLines(rawInput).length === 0}
                style={{
                  height: 40,
                  padding: '0 22px',
                  borderRadius: 10,
                  border: 'none',
                  background:
                    parseEmailLines(rawInput).length === 0 ? '#BABABA' : '#2E5F56',
                  color: '#FFFFFF',
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: parseEmailLines(rawInput).length === 0 ? 'default' : 'pointer',
                }}
              >
                Controleer lijst →
              </button>
            </div>
          </>
        )}

        {/* ── Stap 2: Preview & bevestigen ── */}
        {step === 'preview' && (
          <>
            <div style={{ marginBottom: 14, fontSize: 13.5, color: '#5A5A5A' }}>
              Controleer de lijst en pas namen aan indien gewenst. Daarna klik je op{' '}
              <strong>"Verstuur uitnodigingen"</strong>.
            </div>

            <div
              style={{
                border: '1px solid #E0DDD8',
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 16,
              }}
            >
              {/* Table header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 80px',
                  background: '#F7F5F0',
                  padding: '8px 12px',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#9A9A9A',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                <span>E-mailadres</span>
                <span>Naam (optioneel)</span>
                <span>Status</span>
              </div>

              {/* Rows */}
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {rows.map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 80px',
                      padding: '8px 12px',
                      gap: 8,
                      borderTop: '1px solid #F0EDE8',
                      alignItems: 'center',
                      background:
                        row.status === 'sent'
                          ? 'rgba(62,203,181,0.06)'
                          : row.status === 'error'
                          ? 'rgba(224,82,82,0.05)'
                          : row.status === 'already_sent'
                          ? 'rgba(154,154,154,0.05)'
                          : '#FFFFFF',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12.5,
                        color: '#1C1C1C',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.email}
                    </span>
                    <input
                      type="text"
                      value={row.displayName}
                      onChange={(e) => updateRow(i, { displayName: e.target.value })}
                      placeholder="Voornaam"
                      disabled={sending || row.status === 'sent' || row.status === 'already_sent'}
                      style={{
                        height: 30,
                        padding: '0 8px',
                        borderRadius: 6,
                        border: '1px solid #E0DDD8',
                        fontSize: 12.5,
                        color: '#1C1C1C',
                        background: '#FAFAF8',
                        outline: 'none',
                      }}
                    />
                    <StatusBadge row={row} />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={reset}
                disabled={sending}
                style={{
                  flex: 1,
                  height: 42,
                  borderRadius: 10,
                  border: '1.5px solid #E0DDD8',
                  background: '#FFFFFF',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: '#1C1C1C',
                  cursor: sending ? 'default' : 'pointer',
                }}
              >
                Terug
              </button>
              <button
                onClick={handleSendAll}
                disabled={sending}
                style={{
                  flex: 2,
                  height: 42,
                  borderRadius: 10,
                  border: 'none',
                  background: sending ? '#8DA8A0' : '#2E5F56',
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: '#FFFFFF',
                  cursor: sending ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {sending ? (
                  <>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        border: '2px solid rgba(255,255,255,0.4)',
                        borderTopColor: '#FFFFFF',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                    Versturen…
                  </>
                ) : (
                  `Verstuur ${rows.length} uitnodiging${rows.length !== 1 ? 'en' : ''}`
                )}
              </button>
            </div>
          </>
        )}

        {/* ── Stap 3: Klaar ── */}
        {step === 'done' && (
          <>
            <div
              style={{
                textAlign: 'center',
                padding: '20px 0 16px',
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1C1C1C', marginBottom: 6 }}>
                Klaar!
              </div>
              <div style={{ fontSize: 13.5, color: '#5A5A5A', lineHeight: 1.6 }}>
                {sentCount > 0 && (
                  <>
                    <span style={{ color: '#2E5F56', fontWeight: 700 }}>{sentCount}</span> uitnodiging
                    {sentCount !== 1 ? 'en' : ''} verstuurd.{' '}
                  </>
                )}
                {alreadyCount > 0 && (
                  <>
                    <span style={{ color: '#9A9A9A', fontWeight: 700 }}>{alreadyCount}</span> al eerder uitgenodigd.{' '}
                  </>
                )}
                {errorCount > 0 && (
                  <>
                    <span style={{ color: '#E05252', fontWeight: 700 }}>{errorCount}</span> mislukt.
                  </>
                )}
              </div>
              {sentCount > 0 && (
                <div style={{ fontSize: 12, color: '#9A9A9A' }}>
                  De Cloud Function stuurt automatisch de welkomstmail met onboarding-link.
                  Het e-mailadres wordt ook toegevoegd aan de toegangslijst zodat de persoon kan inloggen.
                </div>
              )}
            </div>

            {/* Summary tabel */}
            {rows.length > 0 && (
              <div
                style={{
                  border: '1px solid #E0DDD8',
                  borderRadius: 10,
                  overflow: 'hidden',
                  marginBottom: 16,
                }}
              >
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {rows.map((row, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        borderTop: i === 0 ? 'none' : '1px solid #F0EDE8',
                        background: '#FFFFFF',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12.5, color: '#1C1C1C' }}>{row.email}</div>
                        {row.displayName && (
                          <div style={{ fontSize: 11, color: '#9A9A9A' }}>{row.displayName}</div>
                        )}
                      </div>
                      <StatusBadge row={row} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={reset}
              style={{
                width: '100%',
                height: 42,
                borderRadius: 10,
                border: '1.5px solid #E0DDD8',
                background: '#FFFFFF',
                fontSize: 13.5,
                fontWeight: 600,
                color: '#1C1C1C',
                cursor: 'pointer',
              }}
            >
              Nieuwe batch versturen
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

function StatusBadge({ row }: { row: InviteRow }) {
  const map: Record<InviteRow['status'], { label: string; color: string; bg: string }> = {
    idle: { label: 'Wacht', color: '#9A9A9A', bg: 'rgba(154,154,154,0.1)' },
    sending: { label: '…', color: '#4F6660', bg: 'rgba(79,102,96,0.1)' },
    sent: { label: 'Verstuurd', color: '#2E5F56', bg: 'rgba(62,203,181,0.12)' },
    already_sent: { label: 'Al gedaan', color: '#9A9A9A', bg: 'rgba(154,154,154,0.1)' },
    error: { label: 'Fout', color: '#E05252', bg: 'rgba(224,82,82,0.1)' },
  };
  const s = map[row.status];
  return (
    <span
      title={row.error}
      style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        color: s.color,
        background: s.bg,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

export default AdminEmailInviteTool;
